import Clipboard from 'clipboard';
import DOMUtils from '../../ts/utils/DOMUtils';
import ValueUtils from '../../ts/utils/ValueUtils';

interface IAnnotatedLine {
    line: string;
    lineNumber: number,
    annotation: any,
    annotationElement: Element
}

const annotationDescriptionStartRegex = /^\#([A-Z]|\d{1,2}):?\s+-?/;
const cueballRegEx = /#([0-9]{1,2}|[A-Z])\s*$/;
const lineGroupReferenceRegEx = /group([0-9]{1,2}|[A-Z])/;

export default class AnnotatedCodeListing {
    private codeArea: HTMLElement;
    private listingContainer: HTMLElement;
    private codeAnnotationContainer: HTMLElement;
        
    private rawSourceCode: string;
    private source: string;
    private sourceLines: string[];
    private annotations: any;
    private annotatedLines: IAnnotatedLine[]; 
    private annotationsLinesMap: {[name: string]: { annotatedLines: IAnnotatedLine[], groups: IAnnotatedLine[][]}};

    private references: any[];
    private styles: any[];
    
    constructor(listingContainer: HTMLElement){
        if(listingContainer == null || listingContainer.classList.contains("processed-listing")){
            return;
        }
        
        let rawSourceCode = "";

        this.handleAnnotationClick = this.handleAnnotationClick.bind(this);
        this.handleAnnotationMouseEnter = this.handleAnnotationMouseEnter.bind(this);
        this.handleAnnotationMouseLeave = this.handleAnnotationMouseLeave.bind(this);
        
        this.handleLineClick = this.handleLineClick.bind(this);
        this.handleLineContainerMouseEnter = this.handleLineContainerMouseEnter.bind(this);
        this.handleLineContainerMouseLeave = this.handleLineContainerMouseLeave.bind(this);
        
        this.codeArea = listingContainer.querySelector(".code-area") as HTMLElement;
        if(this.codeArea) { 
            const dataCode = this.codeArea.getAttribute("data-code");
            if(dataCode) {
                rawSourceCode = atob(dataCode);
                this.codeArea.textContent = dataCode;
            }
            else {
                rawSourceCode =  ((this.codeArea as any).value || this.codeArea.textContent).trim(); 
            }
        }
        
        this.rawSourceCode = rawSourceCode;
        this.listingContainer = listingContainer;
        (this.listingContainer as any).annotatedListing = this;

        this.sourceLines = [];
        this.source = "";

        this.annotations = {};
        this.annotatedLines = [];
        this.references = [];
        this.annotationsLinesMap = {};
        this.styles = [];
        
        this.createCodeAnnotationContainer();
        this.createListingEnvironment();
        this.highlightCodeArea();

        this.handleClick = this.handleClick.bind(this);
        document.addEventListener("click", this.handleClick);
        listingContainer.classList.add("processed-listing");
    }

    public clean(){
        this.unsubscribeEvents();
        
        this.listingContainer = null;
    
        this.codeArea = null;

        this.annotations = null;
        this.annotatedLines = null; 
        this.annotationsLinesMap = null;
    }

    public clearCodeHighligths(){
        const lineGroups = this.listingContainer.querySelectorAll(".listing-line-group");
        DOMUtils.removeClassFromElements(lineGroups, "selected");
        DOMUtils.removeClassFromElements(lineGroups, "highlighted");
        DOMUtils.removeClassFromElements(lineGroups, "pre-selected");
        DOMUtils.removeClassFromElements(this.listingContainer.querySelectorAll(".hovered"), "hovered");
    }

    private createCodeAnnotationContainer(){
        this.codeAnnotationContainer = document.createElement("div");
        this.codeAnnotationContainer.classList.add("code-annotation-container");
        this.codeAnnotationContainer.innerHTML = "<div class='annotation-description'></div>";

        this.codeArea.parentElement.appendChild(this.codeAnnotationContainer);
    }

    private createListingEnvironment(){
        if(this.listingContainer.querySelector(".listing-environment-container") != null) { return ; }
        
        const listingEnvironmentContainer = document.createElement("div");
        listingEnvironmentContainer.classList.add("listing-environment-container");

        listingEnvironmentContainer.innerHTML = /*html */`
            <div class="listing-environment-toolbar" style="display:none;">
                <span data-tooltip class="button-copy button radius clickable-link-like">copy</span>
                <span class="listing-environment-toolbar-message-container"></span>
            </div>
        `;
        this.listingContainer.appendChild(listingEnvironmentContainer);

        this.createClipboard();
    }

    private createClipboard(){
        try {
            const listingEnvironmentToolbar = this.listingContainer.querySelector(".listing-environment-toolbar") as HTMLElement;
            if(Clipboard.isSupported()){
                listingEnvironmentToolbar.style.display = "inline-block";
                const clipboard = new Clipboard(this.listingContainer.querySelector(".button-copy") , {
                    text: () => this.source
                });
            
                clipboard.on('success', e => {
                    this.showMessageOnCopyButton("copied!");
                    e.clearSelection();
                    (document.activeElement as HTMLElement).blur();
                });
            
                clipboard.on('error', e => {
                    this.showMessageOnCopyButton("error");
                    (document.activeElement as HTMLElement).blur();
                });
            }
        }
        catch(e){}
    }

    private showMessageOnCopyButton(message: string){
        const messageContainer = this.listingContainer.querySelector(".listing-environment-toolbar-message-container");

        messageContainer.textContent = message;

        setTimeout(() => {
            messageContainer.textContent = "";
        }, 1000);
    }

    private highlightCodeArea(){
        if(this.codeArea && this.codeArea.classList && this.codeArea.classList.contains("highlighted")){
            return;
        }
        
        this.processLines();
        this.buildReferenceAnnotatedLinesMap();
        
        const source = (this.source || "").replace(/\r/g, "").trim();
        
        const preElement = document.createElement("pre");
        preElement.classList.add("code-area");
        
        const codeElement = document.createElement("code");
        codeElement.classList.add("hljs");
        codeElement.textContent = source;

        preElement.appendChild(codeElement);
        
        this.codeArea.parentNode.replaceChild(preElement, this.codeArea);
        this.codeArea = preElement;

        this.codeArea.classList.add("highlighted");
        
        this.createLineNumbers();
        this.createAnnotationsAndLines();
    }

    private processLines() {
        if(!this.rawSourceCode) { return; }
        
        let sourceCode = this.rawSourceCode.trim();

        const annotationsContainer = this.listingContainer.querySelector(".code-annotations-overlay-container");

        if(annotationsContainer && annotationsContainer.getAttribute("data-annotations")){
            sourceCode += "\n" + atob(annotationsContainer.getAttribute("data-annotations"));
        }

        const lines = sourceCode.split(/\r\n|\r|\n/g);
        
        let isProcessingStyles = false;
        let haveAnnotationDefinitionsStarted = false;
        let isStyleToken = false;
        let reference;
        
        for(let i = 0; i < lines.length; i++) {
            const line = lines[i];

            isStyleToken = (line.indexOf("!@%STYLE%@!") > -1);
            //figure out if we are processing styles and store the style information if we are
            if (isProcessingStyles && !isStyleToken) {
                //this.styles.push(JSON.parse(line));
            }
            else {
                const trimmedLine = line.trim();
                const lineMatch = line.match(annotationDescriptionStartRegex);
                const trimmedLineMatch = trimmedLine.match(annotationDescriptionStartRegex);
                if (lineMatch || trimmedLineMatch) {
                    haveAnnotationDefinitionsStarted = true;
                    //E.g: "#23 Some description" "#2 Some description"
                    if(lineMatch != null && lineMatch.length >= 2){
                        reference = lineMatch[1];

                        this.annotations[reference] = {
                            reference: reference,
                            descriptions: [line.substr(lineMatch[0].length).replace(/^:(\s)+/, "")]
                        };
                    }
                    else if(trimmedLineMatch != null && trimmedLineMatch.length >= 2){
                        reference = trimmedLineMatch[1];

                        this.annotations[reference] = {
                            reference: reference,
                            descriptions: [trimmedLine.substr(trimmedLineMatch[0].length).replace(/^:(\s)+/, "")]
                        };
                    }
                }
                //Second line of the annotation description, does not have a starting #
                //Should just be added to the previous annotation
                else if (haveAnnotationDefinitionsStarted && reference && this.annotations[reference] && line.trim()) {
                    this.annotations[reference].descriptions.push(line.trim().replace("!@%STYLE%@!", ""));
                }
                else { //normal code line
                    if(!isStyleToken){
                        this.sourceLines.push(line);
                        this.annotatedLines.push({
                            line: line,
                            lineNumber: i,
                            annotation: null,
                            annotationElement: null
                        });
                    }
                }
            }
            //switch ON/OFF style processing
            if (line.indexOf("!@%STYLE%@!") > -1) {
                isProcessingStyles = !isProcessingStyles;
            }
        }

        for(const reference in this.annotations) {
            this.annotations[reference].description = this.annotations[reference].descriptions.join(" ").trim();
        }

        for(let i = 0; i < this.sourceLines.length; i++) {
            const sourceLine = this.sourceLines[i];
            const annotationMatch = sourceLine.match(cueballRegEx);

            if(annotationMatch) {
                const reference = annotationMatch[1];

                this.sourceLines[i] = ValueUtils.trimEnd(sourceLine.substr(0, sourceLine.indexOf(annotationMatch[0])));

                if(!this.annotations[reference]){
                    this.annotations[reference] = {
                        reference: reference,
                        description: ""
                    }
                }

                this.annotatedLines[i].annotation = this.annotations[reference];
            }
        }   

        this.source = this.sourceLines.join("\r\n");
    }

    private buildReferenceAnnotatedLinesMap() {
        this.annotationsLinesMap = {};

        for(let lineIndex = 0; lineIndex < this.annotatedLines.length; lineIndex++) {
            const annotatedLine = this.annotatedLines[lineIndex];
            const annotation = annotatedLine.annotation;

            if(!annotation) { continue; }

            const reference = annotation.reference;

            if(!this.annotationsLinesMap[reference]){
                this.annotationsLinesMap[reference] = {
                    annotatedLines: [],
                    groups: []
                };
            }

            this.annotationsLinesMap[reference].annotatedLines.push(annotatedLine);
        }

        for(let reference in this.annotationsLinesMap) {
            if(!this.annotationsLinesMap.hasOwnProperty(reference)) { continue; }

            const mapping = this.annotationsLinesMap[reference];
            const annotatedLines = mapping.annotatedLines;
            const groups = mapping.groups = [];

            if(annotatedLines.length <= 0) { continue; }
            if(annotatedLines.length === 1){
                groups.push([annotatedLines[0]]);
                continue;
            }

            const linesToBeAdded = [];
            for(let i = 0; i < annotatedLines.length - 1; i++){
                const currentLine = annotatedLines[i];
                const nextLine = annotatedLines[i + 1];

                let linesToBeAnnotated = [];
                for(let j = currentLine.lineNumber + 1; j < nextLine.lineNumber; j++){
                    const annotatedLine = this.annotatedLines[j];
                    if(!annotatedLine){ continue; }
                    
                    if(!annotatedLine.annotation){
                        linesToBeAnnotated.push(annotatedLine);
                    }
                    else {
                        linesToBeAnnotated = [];
                        break;
                    }   
                }

                for(let j = 0; j < linesToBeAnnotated.length; j++){
                    linesToBeAnnotated[j].annotation = currentLine.annotation;
                }

                linesToBeAdded.push(...linesToBeAnnotated);
            }

            annotatedLines.push(...linesToBeAdded);
            annotatedLines.sort((a, b) => a.lineNumber - b.lineNumber);

            groups.push([annotatedLines[0]]);

            for(let i = 1; i < annotatedLines.length; i++){
                const previousLine = annotatedLines[i-1];
                const currentLine = annotatedLines[i];

                if(previousLine.lineNumber + 1 === currentLine.lineNumber){
                    groups[groups.length-1].push(currentLine);
                }
                else {
                    groups.push([currentLine]);
                }
            }
        }
    };

    /******************    UI BUILDING    *********************/
    private createLineNumbers(){
		const lines = this.getCountLines(this.source);

		if (lines > 0) {
			let linesHtml = '';
			for (let i = 0; i < lines; i++) {
				linesHtml += `<div class="line-number line-number-${i+1}">${i + 1}</div>`;
            }
            
            const codeElement = document.createElement("code");
            codeElement.classList.add("hljs");
            codeElement.classList.add("hljs-line-numbers");
            codeElement.style.cssFloat = "left";

            codeElement.innerHTML = linesHtml;
            this.codeArea.insertBefore(codeElement, this.codeArea.children[0]);
		}
    }

    private getCountLines(text) {
        if (text.length === 0) { return 0; }

		return text.trim().split(/\r\n|\r|\n/g).length;
	}

    private createAnnotationsAndLines() {
        if(!this.listingContainer) { return; }
        
        const annotationsContainer = this.listingContainer.querySelector(".code-annotations-overlay-container");
        if(annotationsContainer == null) { return; }
        
        let linesHtml = "";

        const referenceOffsetMap:{[reference:string]:{[index:number]: {firstLineOffset: number, lastLineEnd: number}}} = {};

        for(let reference in this.annotationsLinesMap){
            const mapping = this.annotationsLinesMap[reference];
            referenceOffsetMap[reference] = {};

            for(let groupIndex = 0; groupIndex < mapping.groups.length; groupIndex++){
                const group = mapping.groups[groupIndex];
                
                const firstLine = group[0];
                const lastLine = group[group.length - 1];
                
                const firstLineNumber = this.codeArea.querySelector(`.line-number-${firstLine.lineNumber + 1}`) as HTMLElement;
                const lastLineNumber = this.codeArea.querySelector(`.line-number-${lastLine.lineNumber + 1}`)  as HTMLElement;
    
                if(firstLineNumber != null && lastLineNumber != null){
                    referenceOffsetMap[reference][groupIndex] = {
                        firstLineOffset: DOMUtils.getElementPosition(firstLineNumber).top,
                        lastLineEnd: DOMUtils.getElementPosition(lastLineNumber).top + DOMUtils.getElementOuterHeight(lastLineNumber)
                    }
                }
                else {
                    console.log("Couldn't build all lines in listing", this.source);
                }
            }
        }
        
        for(let reference in this.annotationsLinesMap) {
            const mapping = this.annotationsLinesMap[reference];

            const annotation = mapping.annotatedLines[0].annotation;

            for(let groupIndex = 0; groupIndex < mapping.groups.length; groupIndex++){
                const group = mapping.groups[groupIndex];

                const firstLine = group[0];
                const lastLine = group[group.length - 1];

                if(!referenceOffsetMap[reference]){
                    continue;
                }
    
                const position = referenceOffsetMap[reference][groupIndex];
                if(!position) { continue; }
    
                linesHtml += `<div class="line-container listing-line-group group${reference}" 
                                   style="top: ${position.firstLineOffset}px; height:${position.lastLineEnd - position.firstLineOffset + 1}px"></div>`
                
                const annotationContainer = document.createElement("div");
                annotationContainer.classList.add("annotation-container");
                annotationContainer.setAttribute("title", annotation.description);
                annotationContainer.style.top = `${position.firstLineOffset}px`;
                annotationContainer.style.height = `${(position.lastLineEnd - position.firstLineOffset + 1)}px`;
                annotationContainer.innerHTML = `<span class='annotation-label'>${annotation.reference}</span>`;
    
                annotationsContainer.appendChild(annotationContainer);
                if(!annotation.labels){ annotation.labels = [];}
                annotation.labels.push(annotationContainer);
    
                for(let i = 0; i < group.length; i++) {
                    group[i].annotationElement = annotationContainer;
                }
            }
        }

        const linesContainer = document.createElement("div");
        linesContainer.innerHTML = linesHtml;

        for(let i = linesContainer.children.length - 1; i >= 0; i--){
            this.codeArea.insertBefore(linesContainer.children[i], this.codeArea.children[0]);
        }

        
        this.registerAnnotationEvents();
    }

    private registerAnnotationEvents() {        
        const annotationsContainer = this.listingContainer.querySelector(".code-annotations-overlay-container");
        
        if(annotationsContainer != null){
            const annotations = annotationsContainer.children;
            for(let i = 0; i < annotations.length; i++){
                annotations[i].addEventListener("click", this.handleAnnotationClick);
                annotations[i].addEventListener("mouseenter", this.handleAnnotationMouseEnter);
                annotations[i].addEventListener("mouseleave", this.handleAnnotationMouseLeave);
            }
        }
        
        const lineContainers = this.listingContainer.querySelectorAll(".line-container");
        for(let i = 0; i < lineContainers.length; i++){
            lineContainers[i].addEventListener("click", this.handleLineClick);
            lineContainers[i].addEventListener("mouseenter", this.handleLineContainerMouseEnter);
            lineContainers[i].addEventListener("mouseleave", this.handleLineContainerMouseLeave);
        }
    }

    private handleAnnotationClick(e: MouseEvent){
        this.goToAnimationStep((e.currentTarget as HTMLElement).textContent.trim(), false, e.currentTarget as Element);
        e.preventDefault();
        e.stopPropagation();
    }

    private handleLineClick(e: MouseEvent){
        const reference = this.getLineGroupReference(e.currentTarget as HTMLElement);
        
        if(!reference) { return; }
        
        this.goToAnimationStep(reference);
    }

    private handleLineContainerMouseEnter(e: MouseEvent){
        this.handleLineWrapperEnter(e.currentTarget as HTMLElement);
    }

    private handleLineContainerMouseLeave(e: MouseEvent){
        this.handleLineWrapperLeave(e.currentTarget as HTMLElement);
    }

    private handleAnnotationMouseEnter(e: MouseEvent){
        const annotation = e.currentTarget as HTMLElement;
        if(annotation.classList.contains("selected")) { return; }

        const lineGroup = this.getWholeLineGroup(annotation);

        if(lineGroup != null) {
            this.handleLineWrapperEnter(lineGroup[0]);
        }
    }

    private handleAnnotationMouseLeave(e: MouseEvent){
        const annotation = e.currentTarget as HTMLElement;
        if(annotation.classList.contains("selected")) { return; }

        const lineGroup = this.getWholeLineGroup(annotation);

        if(lineGroup != null) {
            this.handleLineWrapperLeave(lineGroup[0]);
        }
    }
    
    public getLineGroupReference(line: HTMLElement) {
        const match = this.getLineGroupClassMatch(line);
        return match ? match[1] : "";
    }

    public getLineGroupClass(line: HTMLElement) {
        const match = this.getLineGroupClassMatch(line);
        
        return match ? match[0] : "";
    }

    public getLineGroupClassMatch(line: HTMLElement) {
        if(!line) { return null; }

        const match = line.className.match(lineGroupReferenceRegEx);

        return match && match.length >= 2 ? match : null;
    }

    private handleLineWrapperLeave(lineWrap: HTMLElement) {
      const groupClass = this.getLineGroupClass(lineWrap);

      if(!groupClass) { return; }

      DOMUtils.removeClassFromElements(this.listingContainer.querySelectorAll("." + groupClass), "highlighted");

      const reference = this.getLineGroupReference(lineWrap);

      const annotation = this.annotations[reference];
      if(!annotation) { return; }

      DOMUtils.removeClassFromElements(this.listingContainer.querySelectorAll(".hovered"), "hovered");
    }

    private removeHighlighted(browsable: HTMLElement) {
      if(!browsable) { return; }

      const highlightedLines = browsable.querySelectorAll(".listing-line-group.highlighted");

      if(highlightedLines.length == 0) { return; }

      this.handleLineWrapperLeave(highlightedLines[0] as HTMLElement);
    }

    private handleLineWrapperEnter(line: HTMLElement) {
        this.removeHighlighted(this.listingContainer);

        const groupClass = this.getLineGroupClass(line);

        if(!groupClass) { return; }
        if(line.classList.contains("selected")){ return; }

        this.highlight(this.getLineGroupReference(line));
    }

    private getWholeLineGroup(annotation: HTMLElement): NodeListOf<HTMLElement> {
      if(!annotation) { return null; }

      return this.listingContainer.querySelectorAll(".group" + annotation.textContent.trim());
    }

    public goToAnimationStep(step: string, notifyOthers: boolean = false, targetLabel: Element = null) {
        const annotation = this.annotations[step];
        if(!annotation) { return; }

        this.hideAnnotations();


        const annotationLabels = annotation.labels;
        this.showTip(annotationLabels[0], this.codeAnnotationContainer, targetLabel);

        const lineGroupItems = this.listingContainer.querySelectorAll(".group" + step);
        DOMUtils.addClassToElements(lineGroupItems, "selected");
    }

    private unsubscribeEvents() {
        const annotationsContainer = this.listingContainer.querySelector(".code-annotations-overlay-container");
        if(annotationsContainer != null){
            const annotations = annotationsContainer.children;
            for(let i = 0; i < annotations.length; i++){
                annotations[i].removeEventListener("click", this.handleAnnotationClick);
                annotations[i].removeEventListener("mouseenter", this.handleAnnotationMouseEnter);
                annotations[i].removeEventListener("mouseleave", this.handleAnnotationMouseLeave);
            }
        }

        const lineContainers = this.listingContainer.querySelectorAll(".line-container");
        for(let i = 0; i < lineContainers.length; i++){
            lineContainers[i].removeEventListener("click", this.handleLineClick);
            lineContainers[i].removeEventListener("mouseenter", this.handleLineContainerMouseEnter);
            lineContainers[i].removeEventListener("mouseleave", this.handleLineContainerMouseLeave);
        }

        document.removeEventListener("click", this.handleClick);
    }

    private highlight(step) {
        DOMUtils.addClassToElements(this.listingContainer.querySelectorAll(".group" + step), "highlighted")

        const annotation = this.annotations[step];
        if(!annotation) { return; }

        DOMUtils.addClassToElements(annotation.labels, "hovered");
    };

    private showTip(annotationLabel:HTMLElement, annotationContainer:HTMLElement, targetElement: Element) {
        if(!annotationLabel) { return; }
        
        const annotationDescriptionText = annotationLabel.getAttribute("title");

        const annotationDescription = annotationContainer.querySelector(".annotation-description") as HTMLElement;

        annotationDescription.textContent = annotationDescriptionText;
        annotationDescription.style.width = "auto";

        let offset = {
            left: annotationLabel.offsetLeft,
            top: annotationLabel.offsetTop
        };

        DOMUtils.addClassToElement(annotationContainer, "shown");
        DOMUtils.addClassToElement(annotationContainer, "show-tip");
        DOMUtils.addClassToElement(annotationLabel, "selected");
        DOMUtils.addClassToElement(annotationLabel, "show-tip");

        DOMUtils.addClassToElement(annotationContainer, "vertical");
        DOMUtils.addClassToElement(annotationLabel, "vertical");
        
        offset.top = annotationLabel.offsetTop;
        
        annotationContainer.style.left = "50%";
        annotationContainer.style.right = "unset";
        annotationContainer.style.transform = "translate(-50%, -110%)";
        
        annotationContainer.style.minHeight = annotationLabel.offsetHeight + "px";
        annotationContainer.style.top = offset.top + "px";
    };

    public hideAnnotations(){
        if(this.listingContainer == null){ return; }
        
        DOMUtils.removeClassFromElements(this.listingContainer.querySelectorAll(".annotation-container.selected, .listing-line-group.selected"), "selected");
        DOMUtils.removeClassFromElements(this.listingContainer.querySelectorAll(".annotation-container.show-tip"), "show-tip");

        const codeAnnotationContainers = this.listingContainer.querySelectorAll(".code-annotation-container");
        DOMUtils.removeClassFromElements(codeAnnotationContainers, "show-tip");
        DOMUtils.removeClassFromElements(codeAnnotationContainers, "shown");
    }

    private handleClick(e: MouseEvent){
        const codeAreaContainer = DOMUtils.closest(e.target, ".browsable-container");
        const codeAnnotationContainer = document.getElementById("code-annotation-container");

        if(codeAreaContainer != this.listingContainer
        && codeAreaContainer != null
        && codeAnnotationContainer != null
        && !codeAnnotationContainer.contains(e.target as any)){
            this.hideAnnotations();
        }
    }
}